'use client'

import { consentedTo, ConsentGroup } from '@repo/cookie/consent'
import { getFromStorage, LOCAL_STORAGE_RECENTLY_VIEWED, setInStorage } from '@repo/storage/service'
import { usePathname } from 'next/navigation'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface RecentlyViewedContextType {
  recentlyViewedPathnames: string[]
  removeRecentlyViewed: () => void
  recentlyViewedProductUrlSlugs: string[]
}

const MAX_RECENTLY_VIEWED_ALL = 50
const MAX_RECENTLY_VIEWED_PRODUCTS = 10

const RecentlyViewedContext = createContext<RecentlyViewedContextType | undefined>(undefined)

export const RecentlyViewedProvider = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname()
  const [recentlyViewedPathnames, setRecentlyViewedPathnames] = useState<(typeof pathname)[]>([])

  const recentlyViewedProductUrlSlugs = useMemo(
    () =>
      recentlyViewedPathnames
        ?.filter((entry) => entry?.includes('/product/'))
        ?.flatMap((entry) => entry.split('/')?.filter(Boolean)?.pop() || [])
        ?.slice(0, MAX_RECENTLY_VIEWED_PRODUCTS),
    [recentlyViewedPathnames],
  )

  const addToRecentlyViewed = useCallback((newPathname: string) => {
    setRecentlyViewedPathnames((prev) => {
      return [newPathname, ...prev.filter((path) => path !== newPathname)].slice(0, MAX_RECENTLY_VIEWED_ALL)
    })
  }, [])

  const removeRecentlyViewed = useCallback(() => {
    setRecentlyViewedPathnames([])
  }, [])

  // Add the current pathname to recently viewed upon changes.
  useEffect(() => {
    if (pathname) {
      addToRecentlyViewed(pathname)
    }
  }, [addToRecentlyViewed, pathname])

  // Load recently viewed pathnames from localStorage on initial render.
  useEffect(() => {
    const storedRecentlyViewed = getFromStorage<typeof recentlyViewedPathnames>(LOCAL_STORAGE_RECENTLY_VIEWED, false, true)

    if (storedRecentlyViewed && storedRecentlyViewed.length > 0) {
      setRecentlyViewedPathnames(storedRecentlyViewed)
    }
  }, [])

  // Save recently viewed pathnames to local storage.
  useEffect(() => {
    if (consentedTo(ConsentGroup.PREFERENCES)) {
      setInStorage<typeof recentlyViewedPathnames>(LOCAL_STORAGE_RECENTLY_VIEWED, recentlyViewedPathnames, false, true)
    }
  }, [recentlyViewedPathnames])

  return (
    <RecentlyViewedContext.Provider
      value={{
        recentlyViewedPathnames,
        removeRecentlyViewed,
        recentlyViewedProductUrlSlugs,
      }}
    >
      {children}
    </RecentlyViewedContext.Provider>
  )
}

export const useRecentlyViewed = () => {
  const context = useContext(RecentlyViewedContext)

  if (!context) {
    throw new Error('useRecentlyViewed must be used within a RecentlyViewedProvider')
  }

  return context
}
